import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Typography, Tabs, Tab, Button, Alert, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AppDispatch } from '../../store/store';
import { cardiacTokens as T } from '../../theme/cardiacTheme';
import {
  getGroupAdminReport,
  getGroupAdminReportPdfUrl,
  getGroupAdminReportImages,
  getGroupAdminReportMeasurements,
  getGroupAdminReportDistributions,
  type GroupAdminReportDetail,
  type LiteInstance,
  type Measurement,
  type DistributionRow,
} from '../../rtk-apis/groupAdmin/groupAdminApi';
import GroupAdminLayout from './GroupAdminLayout';
import { thSx, tdSx, tableCardSx, formatDate, formatName, StatusPill, LiteImageGrid } from './GroupAdminShared';

/* One report, read-only: the distributed PDF, the study's clips, saved
   measurements and where the report was sent. Tabs load on first open. */

type TabKey = 'report' | 'images' | 'measurements' | 'distributions';

const Fact: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.ink3 }}>{label}</Typography>
    <Box sx={{ fontSize: 14, color: T.ink, mt: '2px' }}>{value}</Box>
  </Box>
);

const Loading = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
    <CircularProgress />
  </Box>
);

const GroupAdminReportView: React.FC = () => {
  const { reportId = '' } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [report, setReport] = useState<GroupAdminReportDetail | null>(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<TabKey>('report');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [images, setImages] = useState<LiteInstance[] | null>(null);
  const [measurements, setMeasurements] = useState<Measurement[] | null>(null);
  const [distributions, setDistributions] = useState<DistributionRow[] | null>(null);
  const [tabError, setTabError] = useState('');

  useEffect(() => {
    dispatch(getGroupAdminReport(reportId))
      .unwrap()
      .then(setReport)
      .catch((e) => setError(typeof e === 'string' ? e : 'Could not load the report'));
  }, [dispatch, reportId]);

  // Load each tab once, the first time it is shown.
  useEffect(() => {
    if (!report) return;
    const fail = (e: unknown) => setTabError(typeof e === 'string' ? e : 'Could not load this section');
    setTabError('');
    if (tab === 'report' && !pdfUrl) dispatch(getGroupAdminReportPdfUrl(reportId)).unwrap().then(setPdfUrl).catch(fail);
    if (tab === 'images' && !images) dispatch(getGroupAdminReportImages(reportId)).unwrap().then((r) => setImages(r.instances)).catch(fail);
    if (tab === 'measurements' && !measurements) {
      dispatch(getGroupAdminReportMeasurements(reportId)).unwrap().then((r) => setMeasurements(r.measurements)).catch(fail);
    }
    if (tab === 'distributions' && !distributions) {
      dispatch(getGroupAdminReportDistributions(reportId)).unwrap().then((r) => setDistributions(r.distributions)).catch(fail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, report]);

  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const back = (
    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
      Back
    </Button>
  );

  return (
    <GroupAdminLayout title={report ? formatName(report.patientName) : 'Report'} subtitle={report?.clinicName || ' '} actions={back}>
      {error && <Alert severity="error">{error}</Alert>}
      {!report && !error && <Loading />}
      {report && (
        <>
          <Box sx={{ ...tableCardSx, p: '16px 20px', display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(6, 1fr)' }, gap: '14px', mb: '14px' }}>
            <Fact label="Status" value={<StatusPill status={report.status} />} />
            <Fact label="Study date" value={formatDate(report.studyDate)} />
            <Fact label="Type" value={report.studyType === 'stressEcho' ? 'Stress echo' : 'Echo'} />
            <Fact label="Patient ID" value={report.patientId || '—'} />
            <Fact label="Cardiologist" value={report.reportAssignedTo || '—'} />
            <Fact label="Sonographer" value={report.sonographer || '—'} />
          </Box>

          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: '12px', '& .MuiTab-root': { textTransform: 'none' } }}>
            <Tab value="report" label="Report" />
            <Tab value="images" label="Images" />
            <Tab value="measurements" label="Measurements" />
            <Tab value="distributions" label="Distributions" />
          </Tabs>

          {tabError && <Alert severity="error" sx={{ mb: 2 }}>{tabError}</Alert>}

          {tab === 'report' &&
            (pdfUrl ? (
              <Box component="iframe" title="Report" src={pdfUrl} sx={{ width: '100%', height: '78vh', border: `1px solid ${T.line}`, borderRadius: '12px', background: '#fff' }} />
            ) : (
              !tabError && <Loading />
            ))}

          {tab === 'images' && (images ? <LiteImageGrid instances={images} /> : !tabError && <Loading />)}

          {tab === 'measurements' &&
            (measurements ? (
              <Box sx={tableCardSx}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Measurement', 'Value', 'Normal range', 'Category', 'Method'].map((h) => (
                        <Box component="th" key={h} sx={thSx}>{h}</Box>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {measurements.map((m) => (
                      <tr key={m.measurementId}>
                        <Box component="td" sx={tdSx}>{m.parameterName}</Box>
                        <Box component="td" sx={{ ...tdSx, color: m.isAbnormal ? T.amber : T.ink, fontWeight: m.isAbnormal ? 700 : 400 }}>
                          {m.value} {m.unit || ''}
                        </Box>
                        <Box component="td" sx={tdSx}>
                          {m.normalRange ? `${m.normalRange.low ?? '—'} – ${m.normalRange.high ?? '—'}` : '—'}
                        </Box>
                        <Box component="td" sx={tdSx}>{m.category}</Box>
                        <Box component="td" sx={tdSx}>{m.method || '—'}</Box>
                      </tr>
                    ))}
                    {!measurements.length && (
                      <tr>
                        <Box component="td" colSpan={5} sx={{ ...tdSx, color: T.ink3, textAlign: 'center', py: 4 }}>
                          No saved measurements for this report.
                        </Box>
                      </tr>
                    )}
                  </tbody>
                </Box>
              </Box>
            ) : (
              !tabError && <Loading />
            ))}

          {tab === 'distributions' &&
            (distributions ? (
              <Box sx={tableCardSx}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Sent', 'Channel', 'To', 'Format', 'Status'].map((h) => (
                        <Box component="th" key={h} sx={thSx}>{h}</Box>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {distributions.map((d) => (
                      <tr key={d._id}>
                        <Box component="td" sx={tdSx}>{formatDate(d.createdAt)}</Box>
                        <Box component="td" sx={tdSx}>{d.channel === 'email' ? 'Email' : 'Medical Objects'}</Box>
                        <Box component="td" sx={tdSx}>{d.to || '—'}</Box>
                        <Box component="td" sx={tdSx}>{(d.format || '—').toUpperCase()}</Box>
                        <Box component="td" sx={{ ...tdSx, color: d.error ? T.red : T.ink }}>
                          {d.status}
                          {d.error && <Typography sx={{ fontSize: 11.5, color: T.red }}>{d.error}</Typography>}
                        </Box>
                      </tr>
                    ))}
                    {!distributions.length && (
                      <tr>
                        <Box component="td" colSpan={5} sx={{ ...tdSx, color: T.ink3, textAlign: 'center', py: 4 }}>
                          This report has not been distributed.
                        </Box>
                      </tr>
                    )}
                  </tbody>
                </Box>
              </Box>
            ) : (
              !tabError && <Loading />
            ))}
        </>
      )}
    </GroupAdminLayout>
  );
};

export default GroupAdminReportView;
